import React, { useState, useEffect } from 'react';
import { Lock, Bell, FileText, Target, UserCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import SearchBox from './SearchBox';
import { buildOffersUrl, suggestionToParams } from '../lib/searchNav';

const HeroSection = () => {
    const navigate = useNavigate();
    const [rotationIndex, setRotationIndex] = useState(0);

    const rotationTexts = ["en Australie", "au Canada", "aux États-Unis", "au Royaume-Uni", "au Japon", "où tu le souhaites"];

    useEffect(() => {
        const interval = setInterval(() => {
            setRotationIndex((prev) => (prev + 1) % rotationTexts.length);
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="relative w-full overflow-hidden">

            {/* ========== MOBILE HERO ========== */}
            <div className="md:hidden bg-[#BAE6FD] min-h-[500px] w-full flex flex-col items-center pt-8 pb-6 px-6">

                {/* Title */}
                <h1 className="font-inter font-bold text-[36px] leading-[110%] tracking-[-0.02em] text-black text-center">
                    TROUVE TON VIE
                </h1>

                {/* Text Rotator */}
                <div className="font-inter font-bold text-[28px] leading-[120%] tracking-[-0.02em] text-black mt-1 h-[40px]">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={rotationIndex}
                            initial={{ y: 15, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -15, opacity: 0 }}
                            transition={{ duration: 0.15 }}
                            className="text-center"
                        >
                            {rotationTexts[rotationIndex]}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Subtitle */}
                <div className="mt-5 max-w-[340px] text-center">
                    <p className="font-inter font-semibold text-[16px] leading-[22px] text-black">
                        Retrouve toutes les offres de VIE à un seul endroit
                    </p>
                    <p className="font-inter font-normal text-[13px] leading-[18px] text-black/60 mt-1.5">
                        Retrouve les offres publiées sur les sites carrière avant qu'elles ne soient publiées sur Business France
                    </p>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col gap-3 w-full max-w-[300px] mt-8">
                    <Link
                        to="/offres_vie"
                        className="flex items-center justify-center w-full py-3.5 bg-blue-600 text-white rounded-full text-[15px] font-bold shadow-lg shadow-blue-600/25 hover:bg-blue-700 active:scale-[0.98] transition-all"
                    >
                        Explorer les Offres
                    </Link>
                    <Link
                        to="/les-entreprises"
                        className="flex items-center justify-center w-full py-3.5 bg-white text-slate-900 border-2 border-slate-900 rounded-full text-[15px] font-bold hover:bg-slate-50 active:scale-[0.98] transition-all"
                    >
                        Découvrir les Entreprises
                    </Link>
                </div>

                {/* Mascots */}
                <div className="mt-8 w-full max-w-[350px]">
                    <img
                        src="/assets/mascots.jpg"
                        alt="Mascots"
                        className="w-full h-auto object-contain"
                    />
                </div>
            </div>

            {/* Mobile: Trouve+ Section */}
            <div className="md:hidden bg-[#F0F9FF] py-10 px-6">
                {/* Trouve+ Logo */}
                <div className="flex justify-center mb-2">
                    <img src="/assets/Trouve_plus.png" alt="Trouve+" className="h-[50px] w-auto" />
                </div>
                <p className="text-center text-slate-600 text-sm mb-6">
                    Prenez une longueur d'avance pour décrocher votre V.I.E. :
                </p>

                {/* Features Grid */}
                <div className="grid grid-cols-2 gap-4">
                    <FeatureCard
                        icon={<Lock size={28} />}
                        text="Offres exclusives hors BF"
                        gradient="from-violet-100 to-violet-50"
                    />
                    <FeatureCard
                        icon={<Target size={28} />}
                        text="CV Matching intelligent"
                        gradient="from-blue-100 to-blue-50"
                    />
                    <FeatureCard
                        icon={<Bell size={28} />}
                        text="Alertes nouvelles offres"
                        gradient="from-sky-100 to-sky-50"
                    />
                    <FeatureCard
                        icon={<FileText size={28} />}
                        text="Mail de candidature IA"
                        gradient="from-green-100 to-green-50"
                    />
                    <FeatureCard
                        icon={<UserCircle size={28} />}
                        text="Insights recruteurs"
                        gradient="from-rose-100 to-rose-50"
                    />
                </div>
            </div>

            {/* ========== DESKTOP HERO (unchanged) ========== */}
            <div className="hidden md:block bg-[#BAE6FD] h-[675px] w-full relative">
                <div className="max-w-[1440px] mx-auto h-full relative">

                    {/* Main Title */}
                    <h1 className="absolute w-full text-center top-[112px] font-inter font-bold text-[96px] leading-[120%] tracking-[-0.03em] text-black drop-shadow-[0_4px_4px_rgba(0,0,0,0.25)] z-10">
                        TROUVE TON VIE
                    </h1>

                    {/* Text Rotator */}
                    <div className="absolute w-full flex justify-center top-[222px] z-10">
                        <div className="font-inter font-bold text-[65px] leading-[120%] tracking-[-0.03em] text-black">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={rotationIndex}
                                    initial={{ y: 20, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: -20, opacity: 100 }}
                                    transition={{ duration: 0.10 }}
                                >
                                    {rotationTexts[rotationIndex]}
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Subtitle */}
                    <div className="absolute w-full text-center top-[335px] z-10 px-4">
                        <p className="font-inter font-semibold text-[26px] leading-[32px] text-black">
                            Retrouve toutes les offres de VIE à un seul endroit
                        </p>
                        <p className="font-inter font-normal text-[17px] leading-[24px] text-black/55 mt-1">
                            Retrouve les offres publiées sur les sites carrière avant qu'elles ne soient publiées sur Business France
                        </p>
                    </div>

                    {/* Search Bar */}
                    <div className="absolute left-1/2 -translate-x-1/2 top-[396px] w-[686px] h-[48px] bg-[#F5F5F5] border border-[#8E8E8E] rounded-full z-10 transition-all focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-600/20">
                        <SearchBox
                            wrapperClassName="w-full h-full"
                            inputClassName="w-full h-full pl-11 pr-4 bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-[#1E1E1E] placeholder-[#8E8E8E] font-inter font-normal text-[16px] rounded-full"
                            iconClassName="text-[#1E1E1E]"
                            placeholder="Rechercher un VIE par pays, entreprise, intitulé de poste..."
                            onSubmit={(term) => navigate(buildOffersUrl({ search: term }))}
                            onPick={(s) => navigate(buildOffersUrl(suggestionToParams(s)))}
                        />
                    </div>

                </div>
            </div>

            {/* Desktop: Bottom Section (Trouve+) — SaaS Style */}
            <div className="hidden md:block relative bg-[#F0F9FF] h-[340px] w-full border-t border-black/10">
                <div className="max-w-[1440px] mx-auto h-full relative">

                    {/* Trouve+ Card */}
                    <div className="absolute left-[100px] top-1/2 -translate-y-1/2 w-[480px] bg-white/70 backdrop-blur-md rounded-2xl border border-slate-200/60 shadow-lg shadow-slate-200/40 p-5">

                        {/* Header */}
                        <div className="flex items-center justify-between mb-3">
                            <img src="/assets/Trouve_plus.png" alt="Trouve+" className="h-[36px] w-auto" />
                            <div className="flex items-center gap-2.5">
                                <span className="text-[11px] font-bold text-violet-600 bg-violet-50 px-3 py-1 rounded-full uppercase tracking-wider">Premium</span>
                                <Link to="/trouve-plus" className="text-[12px] font-semibold text-white bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 px-3.5 py-1.5 rounded-full transition-all active:scale-[0.97]">
                                    Découvrir →
                                </Link>
                            </div>
                        </div>

                        {/* Features */}
                        <div className="space-y-2">
                            <SaaSFeatureItem
                                icon={<Lock size={18} />}
                                title="Offres exclusives"
                                subtitle="Les offres scrapées sur les sites carrières, hors Business France"
                                color="violet"
                            />
                            <SaaSFeatureItem
                                icon={<Target size={18} />}
                                title="CV Matching intelligent"
                                subtitle="L'IA fait remonter les offres qui collent à votre profil"
                                color="blue"
                            />
                            <SaaSFeatureItem
                                icon={<Bell size={18} />}
                                title="Alertes nouvelles offres"
                                subtitle="Soyez notifié en premier — la rapidité est clé"
                                color="amber"
                            />
                            <SaaSFeatureItem
                                icon={<FileText size={18} />}
                                title="Mail de candidature IA"
                                subtitle="Un mail percutant généré en 1 clic, adapté à chaque offre"
                                color="emerald"
                            />
                            <SaaSFeatureItem
                                icon={<UserCircle size={18} />}
                                title="Insights recruteurs"
                                subtitle="Identifiez la bonne personne à contacter"
                                color="rose"
                            />
                        </div>


                    </div>

                    {/* Mascots */}
                    <div className="absolute left-[649px] -top-[165px] pointer-events-none">
                        <img
                            src="/assets/mascots.jpg"
                            alt="Mascots"
                            className="w-[883px] h-auto object-contain"
                        />
                    </div>

                </div>
            </div>

        </div>
    );
};

// Desktop SaaS Feature Item
const SaaSFeatureItem = ({ icon, title, subtitle, color = "blue" }) => {
    const colorMap = {
        violet: 'bg-violet-100 text-violet-600',
        blue: 'bg-blue-100 text-blue-600',
        amber: 'bg-amber-100 text-amber-600',
        emerald: 'bg-emerald-100 text-emerald-600',
        rose: 'bg-rose-100 text-rose-600',
    };
    return (
        <div className="flex items-start gap-3 group">
            <div className={`w-[34px] h-[34px] rounded-lg flex items-center justify-center flex-shrink-0 ${colorMap[color]} transition-transform group-hover:scale-110`}>
                {icon}
            </div>
            <div className="pt-0.5">
                <p className="font-inter font-bold text-[15px] leading-[20px] text-slate-900">
                    {title}
                </p>
                <p className="font-inter font-normal text-[12px] leading-[16px] text-slate-500 mt-0.5">
                    {subtitle}
                </p>
            </div>
        </div>
    );
};

// Mobile Feature Card
const FeatureCard = ({ icon, text, gradient }) => (
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-5 flex flex-col gap-3`}>
        <div className="text-[#0088FF]">
            {icon}
        </div>
        <span className="font-inter font-semibold text-[14px] leading-[18px] text-slate-900">
            {text}
        </span>
    </div>
);

export default HeroSection;
